import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DotsVerticalIcon from 'mdi-react/DotsVerticalIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import { array, func, object } from 'prop-types';
import React, { useState } from 'react';
import ParameterDialog from 'components/@home/@settings/Tabs/Agents/toolPanels/ParameterDialog';
import ToolPanelSection from 'components/@home/@settings/Tabs/Agents/toolPanels/ToolPanelSection';
import { I18n } from 'utils/i18n';

const ParametersTable = ({ rows, onEdit, onDelete }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{I18n.t('AgenticChatFlow.ParamName')}</TableCell>
          <TableCell>{I18n.t('AgenticChatFlow.ParamType')}</TableCell>
          <TableCell>{I18n.t('AgenticChatFlow.ParamRequired')}</TableCell>
          <TableCell>{I18n.t('AgenticChatFlow.ParamDescription')}</TableCell>
          <TableCell width={48} />
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {I18n.t('AgenticChatFlow.Empty')}
              </Typography>
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row, index) => (
            <ParameterRow
              key={row.clientKey || `${row.name}-${index}`}
              row={row}
              onEdit={() => onEdit(index)}
              onDelete={() => onDelete(index)}
            />
          ))
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

const ParameterRow = ({ row, onEdit, onDelete }) => {
  const [anchor, setAnchor] = useState(null);
  return (
    <TableRow hover>
      <TableCell>{row.name}</TableCell>
      <TableCell>{row.type || 'string'}</TableCell>
      <TableCell>{row.required ? '✓' : '—'}</TableCell>
      <TableCell>{row.description || '—'}</TableCell>
      <TableCell align="right">
        <IconButton size="small" onClick={e => setAnchor(e.currentTarget)}>
          <DotsVerticalIcon size={18} />
        </IconButton>
        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
          <MenuItem
            onClick={() => {
              setAnchor(null);
              onEdit();
            }}
          >
            {I18n.t('AgenticChatFlow.ParamEdit')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchor(null);
              onDelete();
            }}
            sx={{ color: 'error.main' }}
          >
            {I18n.t('AgenticChatFlow.ParamDelete')}
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
};

ParameterRow.propTypes = {
  row: object.isRequired,
  onEdit: func.isRequired,
  onDelete: func.isRequired,
};

ParametersTable.propTypes = {
  rows: array,
  onEdit: func.isRequired,
  onDelete: func.isRequired,
};

const SchemePanel = ({ tool, setTool }) => {
  const [dialog, setDialog] = useState(null);

  const updateList = (field, next) => {
    setTool({ ...tool, [field]: next });
  };

  const openAdd = field => {
    setDialog({
      field,
      index: null,
      initial: {
        name: '',
        type: 'string',
        required: false,
        description: '',
        clientKey: crypto.randomUUID(),
      },
    });
  };

  const openEdit = (field, index) => {
    const list = tool[field] || [];
    setDialog({ field, index, initial: { ...list[index] } });
  };

  const handleSave = param => {
    const { field, index } = dialog;
    const list = [...(tool[field] || [])];
    if (index == null) {
      list.push({ ...param, clientKey: param.clientKey || crypto.randomUUID() });
    } else {
      list[index] = { ...list[index], ...param };
    }
    updateList(field, list);
    setDialog(null);
  };

  const handleDelete = (field, index) => {
    updateList(
      field,
      (tool[field] || []).filter((_, i) => i !== index),
    );
  };

  const renderSection = (field, titleKey, descriptionKey) => (
    <ToolPanelSection
      title={I18n.t(`AgenticChatFlow.${titleKey}`)}
      description={I18n.t(`AgenticChatFlow.${descriptionKey}`)}
      flush
      action={
        <Button
          size="small"
          variant="contained"
          color="primary"
          disableElevation
          startIcon={<PlusIcon size={16} />}
          sx={{ textTransform: 'none', fontWeight: 500, flexShrink: 0 }}
          onClick={() => openAdd(field)}
        >
          {I18n.t('AgenticChatFlow.ParamAdd')}
        </Button>
      }
    >
      <ParametersTable
        rows={tool[field] || []}
        onEdit={i => openEdit(field, i)}
        onDelete={i => handleDelete(field, i)}
      />
    </ToolPanelSection>
  );

  return (
    <Stack spacing={2.5}>
      {renderSection('parameters', 'Inputs', 'InputsDescription')}
      {renderSection('outputs', 'Outputs', 'OutputsDescription')}

      {dialog && (
        <ParameterDialog
          open
          title={
            dialog.index == null
              ? I18n.t('AgenticChatFlow.ParamAdd')
              : I18n.t('AgenticChatFlow.ParamEdit')
          }
          initial={dialog.initial}
          onClose={() => setDialog(null)}
          onSave={handleSave}
        />
      )}
    </Stack>
  );
};

SchemePanel.propTypes = {
  tool: object.isRequired,
  setTool: func.isRequired,
};

export default SchemePanel;
